import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import throttle from 'lodash/throttle';
import { AppContext } from '@prisjakt/ui-app-context';
import { createExperimentHook } from '@prisjakt/ui-experiment';
import ExperimentContext from '@prisjakt/ui-experiment/context';
import { getBrowserState } from '../helpers';
import { getAdContextCookie, updateAdContextCookie } from '../helpers/cookie';
import AdServiceContext from '../services/context';

updateAdContextCookie();

const adContextCookie = getAdContextCookie();

export const AdContext = React.createContext();
AdContext.displayName = 'AdContext';

const useAdExperiment = createExperimentHook('takeover', 'baseline', {
  takeover: 'takeover',
  bigLWithPanorama: 'big-l-panorama',
  bigLWithoutPanorama: 'big-l-no-panorama',
});

const AdContextContainerPreConnected = ({ children, ads, experiments }) => {
  const [browserState, setBrowserState] = React.useState(getBrowserState());
  const adExperiment = useAdExperiment();
  const context = React.useContext(AppContext);

  React.useEffect(() => {
    const resizeHandler = throttle(
      () => setBrowserState(getBrowserState()),
      200
    );
    if (IS_BROWSER) {
      window.addEventListener('resize', resizeHandler);
      window.addEventListener('orientationchange', resizeHandler);
    }
    // Cleanup
    return () => {
      if (IS_BROWSER) {
        window.removeEventListener('resize', resizeHandler);
        window.removeEventListener('orientationchange', resizeHandler);
      }
    };
  }, []);

  const adContext = React.useMemo(
    () => ({
      active: context.market.ads.active,
      market: context.market.code,
      experiments,
      adExperiment,
      state: {},
      actions: {
        log: (...args) => {
          if (adContextCookie.debug) {
            args.unshift('[AdContext]');
            console.log.apply(null, args); // eslint-disable-line no-console
          }
        },
      },
    }),
    [context.market.ads.active, context.market.code, experiments, adExperiment]
  );

  const value = React.useMemo(
    () => ({
      ...adContext,
      state: {
        ...browserState,
        ...ads,
      },
    }),
    [adContext, ads, browserState]
  );

  return <AdContext.Provider value={value}>{children}</AdContext.Provider>;
};

const { element, object, array } = PropTypes;

AdContextContainerPreConnected.propTypes = {
  children: element.isRequired,
  ads: object.isRequired,
  experiments: array,
};

AdContextContainerPreConnected.defaultProps = {
  experiments: [],
};

export const AdContextContainer = connect((state, ownProps) => ({
  ads: state.ads,
  experiments: ownProps.experiments,
}))(AdContextContainerPreConnected);

AdContextContainer.displayName = 'AdContextContainer';

export const AdContextContainerWithExperiments = ({ children }) => (
  <ExperimentContext.Consumer>
    {experiments => {
      const selectedExperimentVariants = Object.keys(
        experiments.selectedVariants
      )
        .map(experiment => ({
          id: experiments.selectedVariants[experiment].optimizeId,
          variant: experiments.selectedVariants[experiment].variantIndex,
        }))
        .filter(experiment => experiment.id);

      return (
        <AdContextContainer experiments={selectedExperimentVariants}>
          {children}
        </AdContextContainer>
      );
    }}
  </ExperimentContext.Consumer>
);

AdContextContainerWithExperiments.propTypes = {
  children: element.isRequired,
};

export function withAds(Component) {
  const wrapper = (props, ref) => {
    return (
      <AdContext.Consumer>
        {adContext => (
          <AdServiceContext.Consumer>
            {adServiceContext => (
              <Component
                {...props}
                ref={ref}
                adContext={adContext}
                adServiceContext={adServiceContext}
              />
            )}
          </AdServiceContext.Consumer>
        )}
      </AdContext.Consumer>
    );
  };

  return React.forwardRef(wrapper);
}
